import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Check,
  ArrowRight,
  Anchor,
  MapPin,
  Zap,
  Package,
  Upload,
  File,
  Building,
  Users,
  MessageSquare,
  BarChart,
  TrendingUp,
  Clock,
  Info,
  Map,
  Filter,
  Navigation,
  Rocket,
  Shield,
  Eye,
  Globe,
  Server,
  Lock,
  BarChart3,
  Smartphone,
  MessageCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Search,
  CreditCard,
} from "lucide-react";
import { useSEOData } from "../components/SEO/SEOComponents";
import api from "../utils/api";
import './landing.css';

// ============================================
// STILI
// ============================================

const styles = {
  bg: "#050507",
  cardBg: "rgba(255, 255, 255, 0.05)",
  cardBorder: "rgba(255, 255, 255, 0.1)",
  textPrimary: "#ffffff",
  textSecondary: "rgba(255, 255, 255, 0.7)",
  textMuted: "rgba(255, 255, 255, 0.5)",
  accent: "#22d3ee",
  accentOrange: "#f97316",
  accentGreen: "#34d399",
};

// ============================================
// COMPONENTI SEO
// ============================================

function DarkSEOFAQ({
  faq,
  title = "Domande Frequenti",
}: {
  faq?: { question: string; answer: string }[];
  title?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  if (!faq || faq.length === 0) return null;
  return (
    <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
      <div className="flex items-center gap-2 px-6 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <HelpCircle style={{ width: "20px", height: "20px", color: "#22d3ee" }} />
        <h2 className="font-semibold" style={{ color: "#fff" }}>{title}</h2>
      </div>
      <div>
        {faq.map((item, index) => (
          <div key={index} style={{ borderBottom: index < faq.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
            <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-4 text-left" style={{ transition: "background 0.2s" }}>
              <span className="font-medium" style={{ color: "#fff" }}>{item.question}</span>
              {openIndex === index ? <span style={{ color: "#22d3ee" }}>▲</span> : <span style={{ color: "#22d3ee" }}>▼</span>}
            </button>
            {openIndex === index && (
              <div className="px-4 pb-4" style={{ color: "rgba(255,255,255,0.7)", paddingTop: "0" }}>{item.answer}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DarkSEOStats({ title, stats }: { title?: string; stats: { value: string; label: string }[] }) {
  return (
    <div className="glass-card p-6" style={{ background: "linear-gradient(180deg, rgba(15, 23, 42, 0.8), rgba(5, 5, 7, 0.9))" }}>
      {title && <h2 className="font-semibold text-white mb-4">{title}</h2>}
      <div className="grid grid-cols-3 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className="text-2xl font-bold" style={{ background: "linear-gradient(135deg, #f97316, #fbbf24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{stat.value}</div>
            <div className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DarkSEODefinitions({ definitions, title = "Glossario" }: { definitions?: { term: string; definition: string }[]; title?: string }) {
  if (!definitions || definitions.length === 0) return null;
  return (
    <div className="glass-card p-6">
      <h2 className="font-semibold text-white mb-4">{title}</h2>
      <div className="space-y-4">
        {definitions.map((item, index) => (
          <div key={index}>
            <dt className="font-medium text-white">{item.term}</dt>
            <dd className="text-sm" style={{ color: "rgba(255,255,255,0.6)", marginTop: "4px" }}>{item.definition}</dd>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// COMPONENTI V1 (mantenuti)
// ============================================

function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 100% 80% at 50% 120%, rgba(34, 211, 238, 0.1) 0%, transparent 50%), radial-gradient(ellipse 80% 60% at 80% 20%, rgba(249, 115, 22, 0.06) 0%, transparent 40%), radial-gradient(ellipse 60% 50% at 20% 80%, rgba(52, 211, 153, 0.05) 0%, transparent 40%)` }} />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`, backgroundSize: "50px 50px" }} />
    </div>
  );
}

function GlassCard({ icon: Icon, title, subtitle, tags }: { icon: React.ElementType; title: string; subtitle: string; tags: string[] }) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #3b82f6, #1d4ed8)" }}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.5)", fontFamily: "JetBrains Mono" }}>{title}</span>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>{subtitle}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, i) => (
          <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)", fontFamily: "JetBrains Mono" }}>{tag}</span>
        ))}
      </div>
    </div>
  );
}

function TargetCard({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="glass-card p-6">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(249, 115, 22, 0.1))" }}>
        <Icon className="w-5 h-5" style={{ color: "#f97316" }} />
      </div>
      <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>{description}</p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, features }: { icon: React.ElementType; title: string; description: string; features: string[] }) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(34, 211, 238, 0.1))" }}>
          <Icon className="w-5 h-5" style={{ color: "#22d3ee" }} />
        </div>
        <h3 className="text-base font-semibold text-white">{title}</h3>
      </div>
      <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>{description}</p>
      <ul className="space-y-2">
        {features.map((feat, i) => (
          <li key={i} className="flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
            <Check className="w-4 h-4" style={{ color: "#34d399" }} />
            <span>{feat}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const seoData = useSEOData();

  const { data: settings = {} } = useQuery(
    "landing-settings",
    () => api.get("/settings").then((res) => res.data?.data ?? res.data ?? {}).catch(() => ({})),
    { staleTime: 5 * 60 * 1000 },
  );

  const { data: publicStats = {} } = useQuery(
    "landing-public-stats",
    () => api.get("/stats/public").then((res) => res.data?.data ?? res.data ?? {}).catch(() => ({})),
    { staleTime: 5 * 60 * 1000 },
  );

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // SEO FAQ da WP
  const seoFaqItems = [
    { question: "Come accedere al listino prezzi riservato per dealer?", answer: "Devi essere registrato e approvato come dealer. Compila il modulo di registrazione, carica partita IVA e visura camerale. L'accesso al listino riservato è attivo entro 24-48h dall'approvazione." },
    { question: "Quali finanziamenti dedicati per i dealer?", answer: "Sea Level collabora con primarie banche e factoring per offrire finanziamenti agevolati a partire dal 3.5% TAN per l'acquisto di barche nuove e usate." },
    { question: "Come diventare dealer ufficiale Sea Level?", answer: "Registrati come dealer, completa il profilo aziendale, carica documentazione. Il nostro team verifica e attiva l'account entro 2 giorni lavorativi." },
  ];

  // FAQ complete (V2 + SEO)
  const allFaqs = [
    ...seoFaqItems,
    { q: "Posso disdire quando voglio?", a: "Sì, nessun vincolo. Paghi tramite bonifico o carta di credito, nessun addebito automatico. Puoi cancellare il tuo account in qualsiasi momento dalle impostazioni." },
    { q: "Come pago?", a: "Tramite bonifico bancario o carta di credito. Ti inviamo la fattura prima dell'inizio di ogni ciclo di fatturazione. Nessun rinnovo automatico o addebito nascosto." },
    { q: "I privati possono vedere gli annunci?", a: "Sì, la piattaforma è pubblica per dare più visibilità SEO e geo-localizzazione ai tuoi annunci. I privati possono vedere gli annunci e mettersi in contatto diretto con te, ma le trattative B2B e i prezzi dealer restano riservati agli iscritti." },
    { q: "Funziona solo in Italia?", a: "Sì, al momento ci concentriamo sul mercato italiano. La piattaforma è progettata per espandersi in futuro." },
    { q: "Posso importare dal mio sito web?", a: "Sì! La nostra importazione avanzata può estrarre i dati direttamente dalle tue fonti (il tuo sito web, i tuoi annunci sui portali esterni) e sincronizzarli nella piattaforma." },
    { q: "Sea Level Intelligence sostituisce il mio perito?", a: "Assolutamente no. Sea Level Intelligence monitora i prezzi attuali su internet, ti segnala i falsi annunci e ti mostra la realtà del mercato. È uno strumento intuitivo che amplifica la tua intelligenza e la tua esperienza, non la sostituisce." },
    { q: "Come funziona la logistica se trovo una barca lontana?", a: "Il nostro network è fatto di professionisti reali. I nostri utenti conoscono i trasportatori, parlano lo stesso slang del settore e si incontrano alle fiere più importanti. Spostare una barca da Nord a Sud per un cliente fidelizzato è parte del lavoro quotidiano." },
    { q: "C'è un'app da scaricare?", a: "Sea Level Traders è una PWA (Progressive Web App): funziona come un'app su telefono e desktop, anche offline. Non serve scaricarla dagli store: basta visitare sealevel.it dal browser e installarla." },
    { q: "Posso usare WhatsApp?", a: "Assolutamente sì! Puoi connettere il tuo numero WhatsApp Business alla piattaforma e comunicare con gli altri dealer senza uscire dall'app. Le notifiche arrivano in tempo reale." },
    { q: "Quanto costa Sea Level Intelligence?", a: "È inclusa in tutti i piani! Mentre altri competitor fanno pagare 30-75€ per ogni quotazione, tu hai valutazioni illimitate incluse nell'abbonamento." },
    { q: "Cos'è l'Esclusione Dealer?", a: "È una funzione che ti permette di selezionare specifici concessionari e nascondere i tuoi annunci alla loro vista. Utile per gestire relazioni commerciali delicate o sostituire dealer senza conflitti." },
    { q: "Cosa succede se la mia barca rimane invenduta per molto tempo?", a: "Non ti lasciamo solo. Sea Level Intelligence attiva automaticamente gli Alert Anti-Stallo: ti avvisiamo via WhatsApp quando un'unità supera la media di giacenza di mercato e ti suggeriamo azioni mirate (come un micro-aggiustamento del prezzo o una campagna di riattivazione) per sbloccare la vendita senza svendere." },
  ];

  const competitors = [
    { name: "Band of Boats", nature: "Mista (Broker+Privati)", price: "80€/mese (25 annunci)", listings: "25-50 (a pagamento)", cats: "Solo barche", intel: "30-75€ a quotazione", import: "Base", double: "Non disponibile", exclusion: "Non disponibile", trade: "Email/Telefono", commission: "Non specificato", lock: "12 mesi" },
    { name: "YachtWorld", nature: "Alto di gamma", price: "Su richiesta", listings: "Limitati", cats: "Solo barche", intel: "Non disponibile", import: "Base", double: "Non disponibile", exclusion: "Non disponibile", trade: "Email/Telefono", commission: "Non specificato", lock: "12 mesi" },
    { name: "iNautia", nature: "Mista", price: "~100€/mese (5 annunci)", listings: "Solo 5", cats: "Solo barche", intel: "Non disponibile", import: "Non disponibile", double: "Non disponibile", exclusion: "Non disponibile", trade: "Email/Telefono", commission: "Non specificato", lock: "12 mesi" },
    { name: "ImpresaPiù / Subito", nature: "Vetrina B2C", price: "Su richiesta", listings: "Variabile", cats: "Solo barche", intel: "Non disponibile", import: "MultiGestionale", double: "Non disponibile", exclusion: "Non disponibile", trade: "Messaggistica base", commission: "Non specificato", lock: "12 mesi" },
  ];

  return (
    <>
      <style>{`
        @keyframes gridPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .landing-bg {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background: #050507 !important;
          z-index: 0 !important;
        }
        .landing-grid {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background-image: 
            linear-gradient(rgba(34, 211, 238, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.4) 1px, transparent 1px) !important;
          background-size: 60px 60px !important;
          background-repeat: repeat !important;
          z-index: 1 !important;
          animation: gridPulse 3s ease-in-out infinite;
          pointer-events: none;
        }
        .landing-anchor {
          position: fixed !important;
          top: 30px !important;
          left: 30px !important;
          z-index: 1 !important;
          pointer-events: none !important;
          opacity: 0.15 !important;
          color: #22d3ee !important;
        }
        .lv2-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(6, 182, 212, 0.15);
          border: 1px solid rgba(6, 182, 212, 0.3);
          color: #22d3ee;
          padding: 8px 16px;
          border-radius: 100px;
          font-size: 13px;
          font-family: 'JetBrains Mono', monospace;
        }
        .lv2-badge-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #34d399;
          box-shadow: 0 0 10px #34d399;
        }
        .lv2-section {
          padding: 80px 24px;
          max-width: 900px;
          margin: 0 auto;
        }
        .lv2-section-narrow {
          padding: 80px 24px;
          max-width: 700px;
          margin: 0 auto;
        }
        .lv2-section-wide {
          padding: 80px 24px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .lv2-section-title {
          font-size: 2.25rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 1rem;
          line-height: 1.2;
        }
        .lv2-section-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
        }
        .lv2-problem-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          padding: 28px;
          text-align: center;
          transition: all 0.3s ease;
        }
        .lv2-problem-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(249, 115, 22, 0.2);
        }
        .lv2-pillar-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 28px;
          transition: all 0.3s ease;
        }
        .lv2-pillar-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(34, 211, 238, 0.3);
          transform: translateY(-4px);
        }
        .lv2-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }
        .lv2-highlight-box {
          background: linear-gradient(180deg, rgba(249, 115, 22, 0.1), transparent);
          border: 1px solid rgba(249, 115, 22, 0.2);
          border-radius: 20px;
          padding: 32px;
        }
        .lv2-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .lv2-table th {
          background: rgba(6, 182, 212, 0.1);
          padding: 14px 16px;
          text-align: left;
          font-weight: 600;
          color: #fff;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .lv2-table td {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.8);
        }
        .lv2-table tr:hover td {
          background: rgba(255, 255, 255, 0.03);
        }
        .lv2-pricing-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 32px;
          transition: all 0.3s ease;
        }
        .lv2-pricing-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(34, 211, 238, 0.3);
          transform: translateY(-4px);
        }
        .lv2-pricing-card-featured {
          border-color: rgba(249, 115, 22, 0.4);
          position: relative;
        }
        .lv2-step-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 32px;
          text-align: center;
          transition: all 0.3s ease;
        }
        .lv2-step-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(34, 211, 238, 0.3);
          transform: translateY(-4px);
        }
        .lv2-step-number {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
          margin: 0 auto 16px;
          background: rgba(6, 182, 212, 0.2);
          color: #22d3ee;
          border: 1px solid rgba(6, 182, 212, 0.3);
        }
        .lv2-faq-item {
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        .lv2-faq-item:last-child {
          border-bottom: none;
        }
        .lv2-faq-question {
          width: 100%;
          text-align: left;
          padding: 20px 0;
          background: none;
          border: none;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: color 0.2s;
        }
        .lv2-faq-question:hover {
          color: #22d3ee;
        }
        .lv2-divider {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin: 80px 0;
        }
        @media (max-width: 768px) {
          .lv2-section-title { font-size: 1.75rem; }
          .lv2-section, .lv2-section-narrow, .lv2-section-wide { padding: 60px 16px; }
        }
      `}</style>

      <div className="landing-bg"></div>
      <div className="landing-grid"></div>
      <div className="landing-anchor"><Anchor size={72} /></div>

      <div style={{ background: "transparent", minHeight: "100vh", color: "#fff", position: 'relative', zIndex: 2 }}>

        {/* ==================== HERO ==================== */}
        <section className="relative min-h-screen flex items-center justify-center py-20" style={{ overflow: "visible" }}>
          <AnimatedBackground />
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <div className="lv2-badge" style={{ marginBottom: '32px' }}>
              <span className="lv2-badge-dot"></span>
              <span>1 Mese Gratis - Nessun vincolo, disdici quando vuoi</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block text-white">La Prima Piattaforma B2B</span>
              <span className="block gradient-text">Che Gestisce Barche, Motori e Accessori.</span>
              <span className="block text-white" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 600, marginTop: '8px' }}>
                Con l'Intelligenza dei Dati.
              </span>
            </h1>

            <p className="text-lg md:text-xl mb-8 max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.7)" }}>
              Sea Level Intelligence monitora i prezzi reali su internet, smaschera i falsi annunci e ti dice chi pubblica cosa e dove. La tua intelligenza amplificata.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/register" className="cta-primary">
                Inizia Ora - Registrazione in 2 minuti
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <p className="text-sm mb-12" style={{ color: "rgba(255,255,255,0.5)", display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
              <span>Pagamento tramite bonifico o carta</span>
              <span>Zero commissioni</span>
              <span>Network esclusivo B2B</span>
            </p>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            <span className="text-xs uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.5)" }}>Scopri</span>
            <ArrowRight className="w-5 h-5 rotate-90" style={{ color: "rgba(255,255,255,0.5)" }} />
          </div>
        </section>

        {/* ==================== PROBLEMA ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Quante ore perdi ogni settimana a...</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            {[
              { icon: Search, text: "Cercare barche su 5 portali diversi?" },
              { icon: TrendingUp, text: "Valutare permute senza dati reali?" },
              { icon: MessageSquare, text: "Gestire trattative via email infinite?" },
              { icon: Zap, text: "Pagare booster per avere visibilità?" },
              { icon: Clock, text: "Lasciare barche ferme in banchina o in cantiere senza una strategia concreta per sbloccarle?" },
            ].map((item, i) => (
              <div key={i} className="lv2-problem-card">
                <item.icon className="w-8 h-8" style={{ color: '#f97316', marginBottom: '16px' }} />
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '15px', fontWeight: 500 }}>{item.text}</p>
              </div>
            ))}
          </div>
          <div className="lv2-highlight-box" style={{ marginTop: '40px', textAlign: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 600, color: '#f97316', margin: 0 }}>Noi abbiamo creato la soluzione.</p>
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== CATEGORIE ==================== */}
        <section className="lv2-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">
              Tutto ciò che Serve al tuo <span className="gradient-text">Business</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>Esplora per categoria: barche, motori e accessori nuovi e usati.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <GlassCard icon={Anchor} title="Barche" subtitle="Nuove e usate" tags={["Gommoni", "Motore", "Vela", "Catamarani", "Geolocalizzazione"]} />
            <GlassCard icon={Zap} title="Motori" subtitle="Nuovi e usati" tags={["Fuoribordo", "Elettrici", "Ricambi", "Garanzia"]} />
            <GlassCard icon={Package} title="Accessori" subtitle="Nuovi e usati" tags={["Elettronica", "Sicurezza", "Abbigliamento"]} />
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== 6 PILASTRI ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Perché Sea Level Traders è diverso da tutti gli altri</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {[
              { icon: Globe, title: "Unico Ecosistema Completo", desc: "Barche, Motori e Accessori in una sola piattaforma. Nessun competitor lo fa.", color: "#22d3ee" },
              { icon: Server, title: "Sea Level Intelligence", desc: "Monitoriamo i prezzi su internet, smascheriamo i falsi, ti diamo i dati giusti. La tua intelligenza amplificata.", color: "#34d399" },
              { icon: TrendingUp, title: "Sblocco Intelligente dell'Invenduto", desc: "Il nostro algoritmo identifica le imbarcazioni a rischio \"stallo\", ti suggerisce pricing dinamico basato sul mercato reale e attiva campagne di riattivazione lead automatizzate.", color: "#f97316" },
              { icon: Upload, title: "Importazione Avanzata", desc: "Estraiamo dati dal tuo sito web e dai tuoi annunci sui portali. Tu pubblichi una volta, noi sincronizziamo tutto.", color: "#22d3ee" },
              { icon: Users, title: "Network Collaborativo", desc: "Nessuna guerra di booster. Tutti i dealer hanno le stesse opportunità. Vince la qualità, non il budget.", color: "#34d399" },
              { icon: Eye, title: "Controllo Visibilità", desc: "Seleziona quali dealer possono vedere i tuoi annunci. Massima discrezione nelle tue relazioni commerciali.", color: "#22d3ee" },
              { icon: Lock, title: "Zero Commissioni", desc: "Paghi l'abbonamento, il 100% del margine è tuo. Nessun costo nascosto.", color: "#f97316" },
            ].map((item, i) => (
              <div key={i} className="lv2-pillar-card">
                <div className="lv2-icon-wrapper" style={{ background: `${item.color}20`, border: `1px solid ${item.color}40` }}>
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== TARGET ==================== */}
        <section className="lv2-section" style={{ maxWidth: '800px' }}>
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">
              Per chi è <span className="gradient-text">Sea Level</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>Progettato per i professionisti del settore nautico.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <TargetCard icon={Building} title="Cantiere Nautico" description="Gestisci il tuo inventario, trova nuovi clienti tra concessionari e rivenditori." />
            <TargetCard icon={Users} title="Concessionario" description="Accedi a un network di cantieri verificati, trova barche per i tuoi clienti." />
            <TargetCard icon={Building} title="Rivenditore" description="Espandi il tuo business con accesso a stock di cantieri partner." />
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== TRASFORMA L'INVENDUTO ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Trasforma l'Invenduto in Liquidità, Senza Svalutare il Brand</h2>
            <p className="lv2-section-subtitle" style={{ maxWidth: '650px', margin: '0 auto' }}>
              Mentre i portali tradizionali ti chiedono di pagare "booster" per spingere annunci fermi, Sea Level agisce sull'intelligenza dei dati per sbloccare la vendita in modo strategico.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              { icon: BarChart3, title: "Analisi Predittiva Reale", desc: "Sea Level Intelligence non si limita a mostrare i prezzi della concorrenza. Stima il tempo probabile di vendita della tua imbarcazione in base allo storico del mercato, aiutandoti a posizionarla correttamente fin dal primo giorno.", color: "#22d3ee" },
              { icon: TrendingUp, title: "Pricing Dinamico Suggerito", desc: "Ricevi alert automatici se il prezzo di listino si discosta dal valore di mercato attuale. Un piccolo aggiustamento basato sui dati può ridurre i tempi di giacenza del 40%, senza regalare il margine.", color: "#34d399" },
              { icon: Rocket, title: "Riattivazione Automatica del Network", desc: "Una barca ferma non deve essere invisibile. Il sistema identifica automaticamente i dealer o i lead nel network che hanno cercato unità simili e attiva campagne di notifica mirate per riaccendere l'interesse.", color: "#f97316" },
            ].map((item, i) => (
              <div key={i} className="lv2-pillar-card">
                <div className="lv2-icon-wrapper" style={{ background: `${item.color}20`, border: `1px solid ${item.color}40` }}>
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== TABELLA COMPARATIVA ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Confronta Sea Level Traders con le altre piattaforme</h2>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)' }}>
            <table className="lv2-table">
              <thead>
                <tr>
                  <th>Funzionalità</th>
                  <th style={{ color: '#22d3ee' }}>Sea Level Traders</th>
                  {competitors.map((c, i) => (<th key={i}>{c.name}</th>))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Natura', slt: '100% B2B Puro', key: 'nature' },
                  { label: 'Prezzo Base', slt: '69€/mese', key: 'price' },
                  { label: 'Annunci', slt: 'Illimitati', key: 'listings' },
                  { label: 'Categorie', slt: 'Barche+Motori+Accessori', key: 'cats' },
                  { label: 'Intelligence', slt: 'Predittiva: Pricing Dinamico, Stima Tempi di Vendita e Alert Anti-Stallo inclusi', key: 'intel' },
                  { label: 'Import Dati', slt: 'JSON/CSV+Estrazione fonti', key: 'import' },
                  { label: 'Doppio Prezzo', slt: 'Incluso (B2C/B2B)', key: 'double' },
                  { label: 'Esclusione Dealer', slt: 'Incluso', key: 'exclusion' },
                  { label: 'Trattative', slt: 'Chat+Proposte+Prenotazioni', key: 'trade' },
                  { label: 'Commissioni', slt: '0%', key: 'commission' },
                  { label: 'Vincolo', slt: 'Nessuno (Bonifico/Carta)', key: 'lock' },
                ].map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#fff' }}>{row.label}</td>
                    <td style={{ color: '#22d3ee', fontWeight: 600 }}>{row.slt}</td>
                    {competitors.map((c, j) => <td key={j}>{(c as any)[row.key]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="lv2-highlight-box" style={{ marginTop: '32px', textAlign: 'center' }}>
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#f97316', margin: 0 }}>
              Mentre gli altri ti fanno pagare per ogni quotazione (30-75€) e booster (16-41€), noi includiamo tutto nell'abbonamento.
            </p>
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== FEATURES ==================== */}
        <section className="lv2-section">
          <div className="grid md:grid-cols-2 gap-5">
            <FeatureCard icon={Upload} title="Gestione Inventario" description="Inserisci e gestisci le tue barche con scheda tecnica completa" features={["Inserimento singolo", "Importazione CSV/JSON", "Stato in tempo reale"]} />
            <FeatureCard icon={Map} title="Geolocalizzazione" description="Trova barche vicino al tuo cantiere in Italia" features={["Mappa interattiva", "Filtri avanzati", "Coordinate GPS"]} />
            <FeatureCard icon={MessageSquare} title="Trattative" description="Proponi prezzi e gestisci trattative in sicurezza B2B" features={["Proposta di prezzo", "Prenotazione", "Messaggistica integrata"]} />
            <FeatureCard icon={BarChart} title="Dashboard" description="Tutto per gestire il tuo business nautico" features={["Statistiche dettagliate", "Esporta CSV", "Visualizzazioni"]} />
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== DUE PIANI PRICING ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Scegli il piano adatto al tuo business</h2>
            <p className="lv2-section-subtitle" style={{ maxWidth: '500px', margin: '0 auto' }}>
              Nessun rinnovo automatico. Paghi tramite bonifico o carta di credito. Disdici quando vuoi.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '800px', margin: '0 auto' }}>
            {/* Piano Partner */}
            <div className="lv2-pricing-card">
              <div style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: 500 }}>Piano Partner</div>
              <div style={{ fontSize: '3rem', fontWeight: 800, color: '#fff', marginBottom: '4px', lineHeight: 1 }}>€69</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>/mese</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '24px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                oppure <strong style={{ color: '#fff' }}>690€/anno</strong> <span style={{ color: '#34d399' }}>(2 mesi gratis)</span>
              </div>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px', lineHeight: 1.6 }}>
                Il motore del tuo business nautico. Perfetto per concessionari e cantieri che vogliono vendere e comprare con intelligenza.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '32px' }}>
                {[
                  "Annunci Illimitati (Barche + Motori + Accessori)",
                  "Sea Level Intelligence Inclusa",
                  "Importazione Avanzata (JSON/CSV)",
                  "Doppio Prezzo (B2C / B2B)",
                  "Network Collaborativo",
                  "Trattative Integrate (Chat, WhatsApp)",
                  "Mappa Interattiva GPS",
                  "Esclusione Dealer",
                  "Statistiche Avanzate",
                  "Zero Commissioni",
                  "Visibilità SEO ai privati",
                ].map((feat, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    <Check className="w-4 h-4" style={{ color: '#34d399', marginTop: '2px', flexShrink: 0 }} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="cta-primary" style={{ width: '100%', justifyContent: 'center' }}>Scegli Piano Partner</Link>
              <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>1 mese gratis per provare</p>
            </div>

            {/* Piano Cantiere */}
            <div className="lv2-pricing-card lv2-pricing-card-featured">
              <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#f97316', color: '#fff', padding: '4px 16px', borderRadius: '100px', fontSize: '12px', fontWeight: 600 }}>PIÙ SCELTO</div>
              <div style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: 500, marginTop: '8px' }}>Piano Cantiere / Enterprise</div>
              <div style={{ fontSize: '3rem', fontWeight: 800, color: '#fff', marginBottom: '4px', lineHeight: 1 }}>€119</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>/mese</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '24px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                oppure <strong style={{ color: '#fff' }}>1.190€/anno</strong>
              </div>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px', lineHeight: 1.6 }}>
                Per chi gestisce volumi e strutture complesse. La soluzione definitiva per cantieri navali e multi-agenzie.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '32px' }}>
                {[
                  "Tutto del Piano Partner",
                  "Multi-Utenza (fino a 5 account staff)",
                  "API Integration (ERP)",
                  "Reportistica Avanzata di Flotta",
                  "Account Manager Dedicato",
                  "Hot Deal Alert Illimitati",
                  "Alert Proattivi Anti-Stallo (Email/WhatsApp)",
                  "Campagne di Riattivazione Lead Automatizzate",
                ].map((feat, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    <Check className="w-4 h-4" style={{ color: '#34d399', marginTop: '2px', flexShrink: 0 }} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="cta-primary" style={{ width: '100%', justifyContent: 'center' }}>Scegli Piano Cantiere</Link>
              <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>1 mese gratis per provare</p>
            </div>
          </div>

          {/* Founder Deal */}
          <div className="lv2-highlight-box" style={{ marginTop: '40px', textAlign: 'center', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#f97316', marginBottom: '8px' }}>Hai già usato il mese gratis?</p>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>
              Per i primi utenti (Founder): passa al pagamento <strong>annuale</strong> e blocca il prezzo a <strong style={{ color: '#f97316' }}>49€/mese (588€/anno) a vita</strong> invece di 69€.
            </p>
            <Link to="/register" className="cta-secondary" style={{ fontSize: '14px', padding: '12px 24px', display: 'inline-flex' }}>Scopri di più</Link>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: "rgba(255,255,255,0.4)" }}>
            <CreditCard className="w-4 h-4 inline mr-1" style={{ verticalAlign: 'middle' }} />
            Pagamenti accettati: Bonifico bancario • Carta di credito
          </p>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== LA VERITÀ SUL PRICING ==================== */}
        <section className="lv2-section-narrow">
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 className="lv2-section-title">La verità sul nostro pricing</h2>
          </div>
          <div className="lv2-highlight-box">
            <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, margin: 0 }}>
              Fino ad oggi abbiamo offerto un piano a 20€/mese. Era un biglietto di ingresso per farvi provare la piattaforma.
              <br /><br />
              Ma Sea Level Traders non è più in fase di test. I nostri primi partner sono <strong style={{ color: '#fff' }}>player europei conosciuti</strong> per reputazione e volumi di vendita. Avevano a disposizione <strong style={{ color: '#fff' }}>tutte le alternative</strong> del mercato: Mondial Broker, YachtWorld, Band of Boats, Inavis, TrovoBarche, ImpresaPiù, Subito.it, iNautia... e hanno scelto <strong style={{ color: '#22d3ee' }}>noi</strong>.
              <br /><br />
              Perché? Perché hanno visto che <strong style={{ color: '#fff' }}>non siamo un altro portale di annunci</strong>. Siamo un <strong style={{ color: '#fff' }}>sistema operativo per il vostro business</strong>: unico ecosistema che gestisce barche, motori e accessori, Sea Level Intelligence che monitora i prezzi reali e smaschera i falsi, importazione avanzata, network collaborativo, controllo visibilità e mappa interattiva.
              <br /><br />
              Per questo abbiamo eliminato i 20€ e lanciato due piani che riflettono il <strong style={{ color: '#fff' }}>vero valore della piattaforma</strong>. Manteniamo il <strong style={{ color: '#34d399' }}>primo mese gratis</strong>, perché siamo sicuri che una volta provata la differenza, non tornerete indietro.
            </p>
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== ESCLUSIONE DEALER ==================== */}
        <section className="lv2-section-narrow">
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 className="lv2-section-title">
              <Eye className="w-8 h-8" style={{ color: '#22d3ee', marginRight: '12px', verticalAlign: 'middle' }} />
              Il Tuo Network, Le Tue Regole
            </h2>
          </div>
          <div className="lv2-highlight-box">
            <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, margin: 0 }}>
              Le relazioni commerciali nel settore nautico sono delicate.
              <br /><br />
              Sei un cantiere e hai bisogno di espandere la tua rete di concessionari? Hai interrotto una collaborazione con un dealer e devi valutarne la sostituzione senza creare conflitti?
              <br /><br />
              Abbiamo sviluppato la funzione <strong style={{ color: '#22d3ee' }}>Esclusione Dealer</strong>: puoi selezionare specifici concessionari e <strong style={{ color: '#fff' }}>nascondere i tuoi annunci alla loro vista</strong>.
              <br /><br />
              <span style={{ color: '#34d399' }}>✓</span> Nessuna visibilità indesiderata verso concorrenti o ex partner
              <br />
              <span style={{ color: '#34d399' }}>✓</span> Massima discrezione quando testi nuovi mercati o sostituisci un dealer
              <br />
              <span style={{ color: '#34d399' }}>✓</span> Gestione pulita delle tue partnership, senza sovrapposizioni o conflitti
              <br /><br />
              Non siamo solo un marketplace. Siamo uno strumento strategico che ti dà il potere di decidere <strong style={{ color: '#fff' }}>chi</strong> può vedere il tuo stock e <strong style={{ color: '#fff' }}>chi</strong> no.
            </p>
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== COME FUNZIONA ==================== */}
        <section className="lv2-section-wide">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Inizia in 3 step</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
            {[
              { num: "01", title: "Registrati", desc: "Scegli se sei cantiere o concessionario, compila i dati della tua attività, verifica l'email.", time: "2 minuti" },
              { num: "02", title: "Esplora", desc: "Vedi tutte le barche disponibili, filtra per zona/prezzo/marca, accedi ai prezzi dealer riservati.", time: "Mappa interattiva" },
              { num: "03", title: "Vendi", desc: "Chat integrata, WhatsApp Business, zero commissioni. Chiudi l'affare più velocemente.", time: "Trattative dirette" },
            ].map((step, i) => (
              <div key={i} className="lv2-step-card">
                <div className="lv2-step-number">{step.num}</div>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{step.title}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: '12px' }}>{step.desc}</p>
                <span style={{ fontSize: '12px', color: '#22d3ee', fontFamily: 'JetBrains Mono, monospace' }}>{step.time}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/register" className="cta-primary" style={{ display: 'inline-flex' }}>
              Inizia Ora
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== FAQ ==================== */}
        <section className="lv2-section-narrow">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="lv2-section-title">Domande Frequenti</h2>
          </div>
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            {allFaqs.map((faq, i) => (
              <div key={i} className="lv2-faq-item">
                <button
                  className="lv2-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ padding: '0 24px' }}
                >
                  <span>{faq.q}</span>
                  {openFaq === i ? (
                    <ChevronUp className="w-5 h-5" style={{ color: '#22d3ee', flexShrink: 0 }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: '#22d3ee', flexShrink: 0 }} />
                  )}
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 24px 20px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: 1.7 }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="lv2-divider"></div>

        {/* ==================== SEO STATS ==================== */}
        <section className="lv2-section-narrow">
          <DarkSEOStats
            title="I nostri numeri"
            stats={[
              { value: publicStats.boats ? `${publicStats.boats}+` : "800+", label: "Natanti B2B" },
              { value: publicStats.dealers ? `${publicStats.dealers}+` : "120+", label: "Partner" },
              { value: publicStats.trades ? `${publicStats.trades}+` : "0", label: "Trattative Avviate" },
            ]}
          />
        </section>

        {/* ==================== SEO DEFINITIONS ==================== */}
        {seoData?.definitions && (
          <section className="lv2-section-narrow">
            <DarkSEODefinitions definitions={[
              ...seoData.definitions,
              { term: "Alert Anti-Stallo", definition: "Sistema automatizzato che notifica il dealer quando un'imbarcazione supera un periodo di giacenza predefinito, suggerendo azioni correttive (pricing o visibilità)." },
              { term: "Pricing Dinamico", definition: "Suggerimento di prezzo basato sull'analisi in tempo reale di migliaia di annunci sul mercato, per massimizzare la velocità di vendita senza sacrificare il margine." },
            ]} />
          </section>
        )}

        <div className="lv2-divider"></div>

        {/* ==================== CTA FINALE ==================== */}
        <section className="lv2-section-narrow">
          <div className="lv2-highlight-box" style={{ textAlign: 'center' }}>
            <h2 className="lv2-section-title" style={{ marginBottom: '16px' }}>Unisciti a una network reale di professionisti</h2>
            <p className="lv2-section-subtitle" style={{ marginBottom: '32px' }}>
              Mentre gli altri vendono visibilità, noi vendiamo efficienza. Il tuo tempo è prezioso. Inizia a usarlo meglio.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px', textAlign: 'left', maxWidth: '400px', margin: '0 auto 32px' }}>
              {[
                "1 mese gratis per provare",
                "Nessun vincolo contrattuale",
                "Disdici quando vuoi",
                "Supporto dedicato via email/WhatsApp",
                "Sea Level Intelligence inclusa",
                "Zero commissioni sulle vendite",
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                  <Check className="w-4 h-4" style={{ color: '#34d399', flexShrink: 0 }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Link to="/register" className="cta-primary" style={{ fontSize: '16px', padding: '20px 40px', display: 'inline-flex' }}>
              Inizia il tuo mese gratis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>
              Registrazione in 2 minuti • Bonifico o carta di credito • Fattura elettronica
            </p>
          </div>
        </section>

        {/* ==================== FOOTER ==================== */}
        <footer style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '60px 24px 40px', background: 'rgba(5,5,7,0.85)' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', justifyContent: 'space-between', marginBottom: '40px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#fff', marginBottom: '16px' }}>Sea Level Traders</h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, maxWidth: '300px' }}>
                  La prima piattaforma B2B che gestisce barche, motori e accessori con Sea Level Intelligence.
                </p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Link Utili</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Link to="/chi-siamo" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Chi Siamo</Link>
                  <Link to="/contatti" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Contatti</Link>
                  <Link to="/manuale" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Manuale</Link>
                  <Link to="/blog" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Blog</Link>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contatti</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <a href="mailto:info@sealevel.it" style={{ fontSize: '14px', color: '#22d3ee', textDecoration: 'none' }}>info@sealevel.it</a>
                  <a href="mailto:dealers@sealevel.it" style={{ fontSize: '14px', color: '#22d3ee', textDecoration: 'none' }}>dealers@sealevel.it</a>
                  <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>www.sealevel.it</span>
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px', display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                © 2026 Sea Level B2B - Boat Trader. Tutti i diritti riservati.
              </p>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Link to="/privacy" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Privacy Policy</Link>
                <Link to="/terms" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Termini e Condizioni</Link>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
